import { identity } from 'lodash'
import React, { ComponentProps, ComponentType, useCallback } from 'react'
import { createContainer } from 'react-tracked'
import { useDeepCompareMemoize } from '~/hooks/useDeepEqualEffect'

type AnyFunction = (...args: any[]) => any

// These types here just help to define the overloading of the objectHookToContext function
// Basically if throwErrorIfProviderIsMissing is false, then State can also be undefined, that's the only difference
type Select<Props, State = Props> = <Selected>(selector: (state: State) => Selected) => Selected
type ProviderAndHook<Props, State = Props> = [
  ComponentType<React.PropsWithChildren<Props>>,
  (() => State) & { select: Select<Props, State> }
]
export function objectHookToContext<Props, State = Props>(
  useHook?: (props: Props) => State
): ProviderAndHook<Props, State>
export function objectHookToContext<Props, State = Props>(
  useHook?: (props: Props) => State
): ProviderAndHook<Props, State | undefined>
/**
 * A util that mimics constate but uses react-tracked for a nicer selector API
 *
 * # Pass a hook that returns an object and the object will be shared to all descendants that use the hook
 *
 * @example
 * const [UserProvider, useUser] = objectHookToContext(() => {
 *   // Any hooks can be used here but I'm just showing a very simple return object
 *   return {
 *     name: 'Tim',
 *     age: 39
 *   }
 * })
 *
 * const MyApp = () => {
 *   return (
 *     <UserProvider>
 *       <UserName />
 *       <UserAge />
 *       <UserAgeDoubled />
 *     </UserProvider>
 *   )
 * }
 *
 * const UserName = () => {
 *   // Updates only when name changes, make sure you don't destructure though
 *   // or the proxy won't be able to detect what fields you've accessed.
 *   const user = useUser()
 *   return <div>{user.name}</div>
 * }
 *
 * const UserAge = () => {
 *   // Updates only when age changes
 *   const user = useUser()
 *   return <div>{user.age}</div>
 * }
 *
 * const UserAgeDoubled = () => {
 *   // Also supports selectors via .select
 *   // Updates only when the selector's computation changes
 *   const ageDoubled = useUser.select(s => s.age * 2)
 *   return <div>{ageDoubled}</div>
 * }
 *
 * # Without a passed hook, it will share all props passed to the provider for quick and easy contexts
 *
 * @example
 * const [Provider, useContext] = objectHookToContext<{ foo: number; bar: string }>()
 *
 * const MyApp = () => {
 *   return (
 *     <Provider foo={1} bar="hello">
 *       <Child />
 *     </Provider>
 *   )
 * }
 *
 * const Child = () => {
 *   const ctx = useContext()
 *   return (
 *     <div>
 *       {ctx.foo} {ctx.bar}
 *     </div>
 *   )
 * }
 */
export function objectHookToContext<Props, State = Props>(useHook: (props: Props) => State = identity) {
  const { Provider, useTrackedState, useSelector } = createContainer<State, () => void, Props>((props: Props) => {
    const result = useHook(props)
    // react-tracked hands this value straight to use-context-selector, whose provider notifies every
    // listener each time the value's *reference* changes. A hook that returns a fresh object every
    // render would notify on every render; combined with React's strict nested-update limit that
    // cascades into "Maximum update depth exceeded". Keep the reference stable while the value is
    // deeply unchanged so listeners are only notified on real data changes.
    //
    // Perf: lodash isEqual short-circuits on reference equality at every level, so the common case
    // (fresh outer object whose fields are referentially stable, e.g. a React Query result + memoized
    // params) costs <1µs per render regardless of payload size. A full deep walk only happens when a
    // field is rebuilt-but-equal each render (~0.4ms per 100 list rows) — the exact case this exists
    // to stabilize, and still far cheaper than notifying every listener every render.
    const stableResult = useDeepCompareMemoize(result)
    return [stableResult, useCallback(() => {}, [])] as const
  })
  const _useTrackedState = useTrackedState
  ;(Provider as { displayName?: string }).displayName = `Provider(${useHook.name})`
  return [Provider, Object.assign(_useTrackedState, { select: useSelector })] as ProviderAndHook<Props, State>
}

/**
 * Same as above but has additional hooks to help with updating via a state tuple.
 *
 * Note: the hook MUST return a state tuple e.g. `return [state, setState]`
 *
 * @example
 * const [NameProvider, useName, useUpdateName] = objectHookToContext(() => useState(''))
 *
 * const MyApp = () => {
 *   return (
 *     <UserProvider>
 *       <UserName />
 *       <Button />
 *     </UserProvider>
 *   )
 * }
 *
 * const UserName = () => {
 *   // Updates only when name changes, make sure you don't destructure though
 *   // or the proxy won't be able to detect what fields you've accessed.
 *   const name = useName()
 *   return <div>{name}</div>
 * }
 *
 * const Button = () => {
 *   const name = useName()
 *   const updateName = useUpdateName()
 *   return <input value={name} onChange={(e) => updateName(e.target.value)} />
 * }
 */
export function stateHookToContext<State, Update extends AnyFunction, Props>(
  useHook: (props: Props) => readonly [State, Update]
) {
  const { Provider, useTrackedState, useSelector, useUpdate } = createContainer<State, Update, Props>(useHook)
  return [
    Provider as ComponentType<React.PropsWithChildren<Props>>,
    Object.assign(useTrackedState, { select: useSelector }),
    useUpdate
  ] as const
}

/**
 * A util to wrap a component in some other components, such as Providers, easily.
 * @example
 * const [PageStateProvider, usePageState] = contextHook(() => { ...do some context stuff... })
 *
 * const MyPageComponent = wrapWith(PageStateProvider)(() => {
 *   // Can access the context here, instead of having to make a wrapper component manually.
 *   const pageState = usePageState()
 *   return <div>Welcome to my Page</div>
 * })
 *
 * // can also pass multiple components
 * wrapWith(ProviderA, ProviderB, ProviderC)(MyWrappedComponent)
 *
 * // Is basically equivalent to
 * <ProviderA>
 *   <ProviderB>
 *     <ProviderC>
 *       <MyWrappedComponent />
 *     </ProviderC>
 *   </ProviderB>
 * </ProviderA>
 *
 * // If you want to pass props to your wrapper components, do so like this:
 * wrapWith([PageStateProvider, { foo: true }])
 *
 * // Is basically equivalent to
 * <PageStateProvider foo={true}>
 *   <WrappedComponent />
 * </PageStateProvider>
 */
export function wrapWith(...components: ComponentType<React.PropsWithChildren>[]) {
  return wrap

  function wrap<Wrapped extends ComponentType<ComponentProps<Wrapped>>>(WrappedComponent: Wrapped): Wrapped {
    function WrappedUpComponent(props: ComponentProps<Wrapped>) {
      return components.reduceRight(
        (acc, cmp) => {
          let Component = cmp
          if (Array.isArray(cmp)) {
            Component = cmp[0]
            return <Component {...cmp[1]}>{acc}</Component>
          }
          return <Component>{acc}</Component>
        },
        // @ts-ignore
        <WrappedComponent {...props} />
      )
    }
    WrappedUpComponent.displayName = `Wrapped(${WrappedComponent.displayName})`

    return WrappedUpComponent as Wrapped
  }
}
